import AddMindMapLeafControl from '../controls/AddMindMapLeaf';
import type ShapeBase from '../shapes/ShapeBase';
import type MindMap from './MindMap';
import {
  MIND_MAP_LEAF_MINIMUM_GAP,
  MINDMAP_ANIMATION_DURATION,
  type MindMapSides,
} from './MindMap';
import type MindMapBranch from './MindMapBranch';
import Shape, { type ShapeOptions } from './Shape';
import { isMindMap, isMindMapBranch, isMindMapLeaf } from './guards';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type FrameBehaviors from './FrameBehaviors';
import { type IPoint } from 'fabric/fabric-impl';
import ToolResetTheme from '../tools/ToolResetTheme';
import { panTo } from '../utils/zoom';
import { CUSTOM_THEME_FIELDS } from './MindMapThemes';
import { calculateAttachmentPosition } from '../utils/connectorAttachment';
import { AttachmentPoint } from '../types';

export interface MindMapLeafOptions extends ShapeOptions {
  useTheme?: boolean;
}

class MindMapLeaf<S extends ShapeBase = ShapeBase> extends Shape<S> {
  type = 'mindMapLeaf';

  lockScalingX = true;
  lockScalingY = true;
  lockRotation = true;
  /** Whether to use the theme styles, set to false when customising the style to stop it being overridden */
  useTheme = true;

  serializedKeys = [...this.serializedKeys, 'useTheme'];

  side?: MindMapSides;

  private previousContainer?: RecordId;

  constructor(options: MindMapLeafOptions) {
    super(options);
    // ShapeBase uses a reference to ShapeBase.defaultTextProps, we don't want to modify those, so making a copy here.
    this.shape.textProps = { ...this.shape.textProps, wrapText: 'wrapGrow' };
    // This is weird... copying the textProps object causes minWidth to become undefined
    this.minWidth = this.width;
    this.useTheme = options.useTheme ?? true;
    this.updateTextProps();

    const isRemote = this.controller.client.processingRemoteOps;
    const isLoaded = this.controller.state.isLoaded;
    if (isRemote && isLoaded) {
      // Reflow mindmap if we were just created by redo/remote
      setTimeout(() => this.getMindMap()?.deferUpdateMindMapLayout(), 1);
    }

    // Track hover state for controls
    this.on('mouseover', () => {
      this.updateControls();
      this.hovered = true;
      this.canvas?.requestRenderAll();
    });
    this.on('mouseout', () => {
      this.hovered = false;
      this.canvas?.requestRenderAll();
    });

    this.on('keydown', eventData => {
      this.handleKeyDown(eventData.e);
    });

    setTimeout(() => this.updateLayoutGaps(), 1);
  }

  static create<S extends ShapeBase = ShapeBase>(options: ShapeOptions) {
    return options.controller.addToCanvas(new MindMapLeaf<S>(options));
  }

  /**
   * Updates the spacing around the leaf so we take up the same amount of space all our children do
   */
  updateLayoutGaps() {
    this.controller.undoManager.withoutUndo(() => {
      const mindmp = this.getMindMap()!;
      let remain = MIND_MAP_LEAF_MINIMUM_GAP;
      const branch = mindmp.getChildBranch(this.id)!;
      if (!branch) {
        console.error('Missing branch', this.id, this);
        return;
      }
      if (['east', 'west'].includes(branch.side)) {
        const target = branch.getScaledHeight();
        const height = this.getScaledHeight();
        remain = Math.max(remain, target - height);
      } else {
        const target = branch.getScaledWidth();
        const width = this.getScaledWidth();
        remain = Math.max(remain, target - width);
      }
      this.set({
        layoutGapBefore: remain / 2,
        layoutGapAfter: remain / 2,
      });
    });
  }

  updateTextProps() {
    this.textProps.wrapText = this.wrapText ? 'wrapGrow' : 'growObject';
  }

  tools(topLevel: boolean = true) {
    const { isArray } = Array;
    const tools = [...super.tools(topLevel)];
    // Add to colours section
    if (isArray(tools[3])) {
      tools[3].unshift(ToolResetTheme);
    } else {
      tools.unshift(ToolResetTheme);
    }
    return tools;
  }

  getPreviousBranch(): MindMapBranch | undefined {
    if (this.previousContainer) {
      const possibleFrame = this.controller.dataStoreObjects.findRecordById(
        this.previousContainer
      );
      if (isMindMapBranch(possibleFrame)) {
        return possibleFrame;
      }
    }
  }

  getPreviousMindMap(): MindMap | undefined {
    return this.getPreviousBranch()?.getMindMap();
  }

  /** Get the branch this leaf is inside */
  getBranch(): MindMapBranch | undefined {
    return this.inFrame() as MindMapBranch | undefined;
  }

  getMindMap(): MindMap | undefined {
    return this.getBranch()?.getMindMap();
  }

  canAddToFrame(frame: FrameBehaviors & fabric.Object): boolean {
    // Don't allow adding to non-MindMapBranch frames
    if (!isMindMapBranch(frame)) {
      return false;
    }

    // Don't allow adding to a child branch of this leaf
    const mindMap = this.getMindMap();
    if (!mindMap) return false;

    const childBranch = mindMap.getChildBranch(this.id);
    if (!childBranch) return true;

    // Check if the target frame is a descendant of this leaf's child branch
    return !this.isDescendantBranch(frame, childBranch);
  }

  private isDescendantBranch(
    targetBranch: MindMapBranch,
    ancestorBranch: MindMapBranch
  ): boolean {
    let currentBranch: MindMapBranch | undefined = targetBranch;

    while (currentBranch) {
      if (currentBranch.id === ancestorBranch.id) {
        return true;
      }

      // Move up to parent branch
      currentBranch = currentBranch.getParentLeaf()?.getBranch();
    }

    return false;
  }

  updateControls() {
    const branch = this.getBranch();
    if (!branch) return;
    if (branch.side === this.side) return;
    this.side = branch.side;

    this.controls = {};
    this.persistentControls = {};

    const controls = this.persistentControls;

    const offset = 0.5;
    switch (branch.side) {
      case 'west':
        controls.cl = new AddMindMapLeafControl({
          x: -offset,
          y: 0,
        });
        controls.ct = new AddMindMapLeafControl({
          x: 0,
          y: -offset,
          mini: true,
        });
        controls.cb = new AddMindMapLeafControl({
          x: 0,
          y: offset,
          mini: true,
        });
        break;

      case 'east':
        controls.cr = new AddMindMapLeafControl({
          x: offset,
          y: 0,
        });
        controls.ct = new AddMindMapLeafControl({
          x: 0,
          y: -offset,
          mini: true,
        });
        controls.cb = new AddMindMapLeafControl({
          x: 0,
          y: offset,
          mini: true,
        });
        break;

      case 'north':
        controls.cl = new AddMindMapLeafControl({
          x: -offset,
          y: 0,
          mini: true,
        });
        controls.cr = new AddMindMapLeafControl({
          x: offset,
          y: 0,
          mini: true,
        });
        controls.ct = new AddMindMapLeafControl({
          x: 0,
          y: -offset,
        });
        break;

      case 'south':
        controls.cl = new AddMindMapLeafControl({
          x: -offset,
          y: 0,
          mini: true,
        });
        controls.cr = new AddMindMapLeafControl({
          x: offset,
          y: 0,
          mini: true,
        });
        controls.cb = new AddMindMapLeafControl({
          x: 0,
          y: offset,
        });
        break;
    }
  }

  deleted(controller: WhiteboardController) {
    const mindMap = this.getMindMap() ?? this.getPreviousMindMap();
    if (!mindMap) {
      console.error('Deleted MindMapLeaf has no MindMap', this);
      return;
    }
    const childBranch = mindMap.getChildBranch(this.id);
    const connectorsToDelete: fabric.Object[] = [];

    // Collect connectors attached to this leaf itself
    if (this.connectorAttachments) {
      this.connectorAttachments.forEach(attachment => {
        if (!connectorsToDelete.includes(attachment.connector)) {
          connectorsToDelete.push(attachment.connector);
        }
      });
    }

    if (childBranch && isMindMapBranch(childBranch)) {
      // Collect all child objects and their connectors
      const leaves = childBranch.leaves;

      // Recursively delete all child objects in the branch
      leaves.forEach(leaf => {
        // Collect connectors attached to this child
        if (leaf.connectorAttachments) {
          leaf.connectorAttachments.forEach(attachment => {
            if (!connectorsToDelete.includes(attachment.connector)) {
              connectorsToDelete.push(attachment.connector);
            }
          });
        }

        if (leaf.deleted) {
          leaf.deleted(controller);
        }
        leaf.fire('deleted');
      });

      // Remove child objects from canvas
      controller.canvas.remove(...leaves);

      // Remove the child branch itself
      if (childBranch.deleted) {
        childBranch.deleted(controller);
      }
      childBranch.fire('deleted');
      controller.canvas.remove(childBranch);
    }

    // Remove all collected connectors
    if (connectorsToDelete.length > 0) {
      connectorsToDelete.forEach(connector => {
        if (connector.deleted) {
          connector.deleted(controller);
        }
        connector.fire('deleted');
      });
      controller.canvas.remove(...connectorsToDelete);
    }

    // Call parent cleanup (but skip connector cleanup since we handled it above)
    this.connectorAttachments.forEach(attachment =>
      attachment.connector.detachObject(this)
    );
    mindMap.deferUpdateMindMapLayout();

    super.deleted(controller);
  }

  setFromRemote(key: KeyPath, value: unknown) {
    if (key === 'container') {
      const mindmap = this.getMindMap()!;
      const oldBranch = this.getBranch();
      const newBranch = mindmap.getBranch(value as RecordId);
      if (oldBranch && oldBranch.side !== newBranch?.side) {
        // We've moved to another side, make sure connectors update
        mindmap.updateConnectorAttachments(this);
      }
    }
    // This is also called via undo, it nullifies the container/branch ID and we need this to cleanup when being deleted via an undo
    if (key === 'container' && this.container) {
      this.previousContainer = this.container;
    }
    super.setFromRemote(key, value);
    this.getMindMap()?.deferUpdateMindMapLayout();
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.shiftKey || (e.key !== 'Enter' && e.key !== 'Tab')) return;
    e.preventDefault();
    e.stopPropagation();

    if (this.textEditing) {
      this.endTextEditing();
    }

    const mindmap = this.getMindMap()!;
    const branch = this.getBranch()!;
    let leaf: MindMapLeaf;

    if (e.key === 'Enter') {
      const index = branch.leaves.indexOf(this);
      leaf = mindmap.createChildInBranch(branch, {}, index + 1);
    } else if (e.key === 'Tab') {
      leaf = mindmap.createChild(this);
    }

    panTo(this.controller, this, { animate: true });
    mindmap.updateMindMapLayout();

    // Start editing once the animation finishes
    setTimeout(() => {
      this.canvas.setActiveObject(leaf);
      leaf.startTextEditing(true);
    }, MINDMAP_ANIMATION_DURATION * 1.1);
  }

  async startTextEditing(selectAll = false) {
    await super.startTextEditing(selectAll);
  }

  endTextEditing() {
    super.endTextEditing();
    if (!this.textEditing) return;
    this.getMindMap()?.deferUpdateMindMapLayout();
  }

  // Override from FrameMemberBehaviors so we can fallback to nearby leaves
  getTargetFrame(e: Event): (FrameBehaviors & fabric.Object) | undefined {
    const p = this.canvas.getPointer(e, true);
    let frame = this.pointerOverFrame(p);
    if (!frame || frame.children.length === 0) {
      // No populated frame under the cursor,
      // instead find the nearest leaf (or mindmap center shape) and attach to that
      const leaf = this.findNearbyLeaf();
      if (isMindMap(leaf)) {
        frame = leaf
          .rootBranches()
          .sort((a, b) => calculateDistance(p, a) - calculateDistance(p, b))[0];
      } else {
        frame = leaf?.getMindMap()?.getChildBranch(leaf.id);
      }
    }

    return frame;
  }

  findNearbyLeaf(): MindMap | MindMapLeaf | undefined {
    // Only apply proximity detection when this leaf is being dragged
    if (!this.objectIsMoving() && !this.frameMemberMoving) {
      return;
    }

    // Get all MindMapLeaf objects on the canvas
    const allLeaves = this.canvas
      .getObjects()
      .filter(obj => isMindMapLeaf(obj) && obj.id !== this.id) as (
      | MindMap
      | MindMapLeaf
    )[];
    allLeaves.unshift(this.getMindMap()!);

    let closestLeaf: MindMap | MindMapLeaf | undefined;
    let closestDistance = Infinity;

    // Find the closest leaf
    for (const leaf of allLeaves) {
      let targetBranch: MindMapBranch | undefined;
      if (isMindMap(leaf)) {
        // Center shape is closest, find which side to attach
        targetBranch = leaf
          .rootBranches()
          .sort(
            (a, b) => calculateDistance(leaf, a) - calculateDistance(leaf, b)
          )[0];
      } else {
        // Ensure the target leaf can accept this leaf as a child
        targetBranch = leaf.getBranch();
      }

      if (targetBranch && this.canAddToFrame(targetBranch)) {
        // Calculate distance between incoming and outgoing edges
        const distance = calculateEdgeProximity(this, leaf, targetBranch);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestLeaf = leaf;
        }
      }
    }

    return closestLeaf;
  }

  _set(keyOrPath: string, value: unknown): fabric.Object {
    const result = super._set(keyOrPath, value);

    const mindmap = this.getMindMap();
    if (keyOrPath === 'useTheme' || CUSTOM_THEME_FIELDS.includes(keyOrPath)) {
      if (!this.useTheme) {
        // Trigger theme reapplication when our colour changes are we're not just inheriting it from the theme
        mindmap?.updateCustomTheme(this);
      }
    }

    if (keyOrPath === 'textContent') {
      mindmap?.deferUpdateMindMapLayout();
    }

    return result;
  }

  initializeShape(changingShape = false) {
    super.initializeShape(changingShape);
    this.controls = {};
  }
}

// Attachment point mappings for each mindmap side
const IN_ATTACHMENT_POINT = {
  east: AttachmentPoint.Left,
  west: AttachmentPoint.Right,
  north: AttachmentPoint.Bottom,
  south: AttachmentPoint.Top,
};
const OUT_ATTACHMENT_POINT = {
  east: AttachmentPoint.Right,
  west: AttachmentPoint.Left,
  north: AttachmentPoint.Top,
  south: AttachmentPoint.Bottom,
};

/**
 * Calculate distance between the incoming edge of the dragged leaf and outgoing edge of target leaf
 */
function calculateEdgeProximity(
  draggedLeaf: MindMapLeaf,
  targetLeaf: MindMapLeaf | MindMap,
  targetBranch: MindMapBranch
): number {
  // Get attachment points for the dragged leaf (incoming edge)
  const draggedBranch = draggedLeaf.getBranch();
  if (!draggedBranch) return Infinity;

  const incomingAttachmentPoint = IN_ATTACHMENT_POINT[draggedBranch.side];
  const outgoingAttachmentPoint = OUT_ATTACHMENT_POINT[targetBranch.side];

  // Calculate the actual edge positions
  const incomingEdgePos = calculateAttachmentPosition(
    draggedLeaf,
    incomingAttachmentPoint,
    { withStandoff: false }
  );

  const outgoingEdgePos = calculateAttachmentPosition(
    targetLeaf,
    outgoingAttachmentPoint,
    { withStandoff: false }
  );

  if (!incomingEdgePos || !outgoingEdgePos) {
    // Fallback to node distance if edge calculation fails
    return calculateDistance(draggedLeaf, targetLeaf);
  }

  // Calculate distance between the edge endpoints
  const dx = incomingEdgePos.x - outgoingEdgePos.x;
  const dy = incomingEdgePos.y - outgoingEdgePos.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function calculateDistance(
  leaf0: fabric.Object | IPoint,
  leaf1: fabric.Object
): number {
  let bounds0: typeof bounds1;
  if ('left' in leaf0) {
    bounds0 = {
      left: leaf0.left,
      top: leaf0.top,
      width: leaf0.getScaledWidth(),
      height: leaf0.getScaledHeight(),
    };
  } else {
    bounds0 = { left: leaf0.x, top: leaf0.y, width: 1, height: 1 };
  }
  const bounds1 = {
    left: leaf1.left,
    top: leaf1.top,
    width: leaf1.getScaledWidth(),
    height: leaf1.getScaledHeight(),
  };

  // Calculate the closest distance between the rectangle boundaries
  const left1 = bounds0.left;
  const right1 = bounds0.left + bounds0.width;
  const top1 = bounds0.top;
  const bottom1 = bounds0.top + bounds0.height;

  const left2 = bounds1.left;
  const right2 = bounds1.left + bounds1.width;
  const top2 = bounds1.top;
  const bottom2 = bounds1.top + bounds1.height;

  // Check for overlap or calculate minimum distance
  const horizontalDistance = Math.max(
    0,
    Math.max(left1 - right2, left2 - right1)
  );
  const verticalDistance = Math.max(
    0,
    Math.max(top1 - bottom2, top2 - bottom1)
  );

  return Math.sqrt(
    horizontalDistance * horizontalDistance +
      verticalDistance * verticalDistance
  );
}

export default MindMapLeaf;
